import {
    Component,
    computed,
    effect,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    signal,
    SimpleChanges,
} from '@angular/core';
import { IExecutorPoint, IMapPoint } from '@lib-typing/map-point.interface';
import { MapService } from './map.service';
import { GroupsService } from '@lib-services/groups.service';
import { MessageService } from '@lib-services/message.service';

declare const ymaps: any;

@Component({
    selector: 'app-map',
    templateUrl: './map.component.html',
    standalone: true,
    styleUrls: ['./map.component.scss']
})
export class MapComponent implements OnChanges {
    @Input() set showFilters(value: boolean) {
        this.showFiltersSignal.set(value); // Обновляем внутренний сигнал
    }

    @Input() set hoveredOrderId(value: number | null) {
        this.highlightMapPoint(value);
        this.handleSelectedOrdersChange(value);
    }

    @Input() selectedOrders: number[] = []; // выделенные заявки

    @Output() pointTouched = new EventEmitter<number | null>(); // Событие, которое будет emit при клике на точку
    @Output() workerEntered = new EventEmitter<number | null>(); // Событие, которое будет emit при клике на точку
    @Output() selectionChanged = new EventEmitter();

    private showFiltersSignal = signal<boolean>(true); // Внутренний сигнал
    public map: any;
    public updateMap = true;
    private selectionMode = false;
    private selectionStartPoint: number[] | null = null;
    private selectionRectangle: any = null;
    private selectedPoints: number[] = [];

    constructor(
        private mapService: MapService,
        private groupsService: GroupsService,
        private messageService: MessageService,
    ) {

        effect(() => {
            this.drawMap(this.mapService.points());
            this.addActiveOrders(this.mapService.activeOrders());
            this.addExecutorsLocation(this.mapService.executorsLocations());
        });

        effect(() => {
            const id = this.groupsService.currentGroupId();
            if (id) {
                this.getGroupCoords(id);
            } else if (this.map) {
                this.map.destroy(); // Уничтожаем карту
                this.map = null; // Обнуляем ссылку
            }
        }, { allowSignalWrites: true });

        const showFiltersComputed = computed(() => this.showFiltersSignal());  // Зависимость от сигнала
        effect(() => {
            showFiltersComputed(); // Чтение computed для активации эффекта
            this.updateMapSize(); // Обновляем размер карты
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['selectedOrders'] &&
            changes['selectedOrders'].currentValue?.length !== changes['selectedOrders'].previousValue?.length
        ) { // реагируем на выделенные заявки
            this.handleSelectedOrdersChange();
        }
    }

    private initializeMap(center: [number, number]): void {
        this.map = new ymaps.Map('map', {
            center: center,
            zoom: 12.5,
            controls: ['zoomControl', 'fullscreenControl']
        });
        this.setupSelectionHandlers();
    }

    private getGroupCoords(id: number): void {
        this.groupsService.getGroup(id).subscribe({
            next: group => {
                const newCenter: [number, number] = [
                    group.location.latitude,
                    group.location.longitude
                ];

                if (this.map) {
                    // Если карта отображадась - меняем центр
                    this.map.setCenter(newCenter);
                } else {
                    // Если карта не показывалась - инициализируем и устанавливаем центр
                    ymaps.ready().then(() => {
                        this.initializeMap(newCenter);
                    });
                }
            }, error: (err) => this.messageService.errorMessage(err.error.message || 'Произошла ошибка')
        });
    }

    // выделяем точки на карте если заявка выделена
    private handleSelectedOrdersChange(value?: number | null): void {
        if (value) {return;}
        this.map?.geoObjects.each((placemark: any) => {
            const orderId = placemark.properties.get('orderId');
            const iconContent = placemark.properties.get('iconContent');

            // распределенные раявки
            if (orderId && iconContent && this.selectedOrders.includes(orderId)) { // выделяем зеленым
                placemark.options.set('preset', 'islands#greenIcon');
            } else if(orderId && iconContent) { // возвращаем синий цвет
                placemark.options.set('preset', 'islands#blueIcon');
            }

            // нераспределенные раявки
            if (orderId && !iconContent && this.selectedOrders.includes(orderId)) { // выделяем зеленым
                placemark.options.set('preset', 'islands#greenCircleDotIcon');
            } else if(orderId && !iconContent) { // возвращаем черный цвет
                placemark.options.set('preset', 'islands#blackCircleDotIcon');
            }
        });
    }

    private setupSelectionHandlers(): void { // метод выделения области на карте
        this.map.events.add('mousedown', (e: any) => {
            if (e.get('button') !== 2) {return;}
            // начинаем выделять по нажатию ПКМ
            this.selectionMode = true; // устанавливаем флаг начала выделения
            this.selectedPoints = [];
            const coords = e.get('coords');
            if (!coords) {return;}

            this.selectionStartPoint = coords; // точка начала выделения области

            if (this.selectionRectangle) {
                this.map.geoObjects.remove(this.selectionRectangle); // очищаем предыдущее значение области выделения
            }

            if (this.selectionStartPoint) {
                this.selectionRectangle = new ymaps.Rectangle([
                    [this.selectionStartPoint[0], this.selectionStartPoint[1]],
                    [coords[0], coords[1]]
                ]
                , {}, {
                    strokeColor: '#FF0000',
                    strokeWidth: 2,
                    strokeStyle: 'solid',
                    fillColor: '#FF0000',
                    fillOpacity: 0.3,
                    interactivityModel: 'default#transparent',
                    cursor: 'crosshair'
                });
            }

            this.map.geoObjects.add(this.selectionRectangle);
        });

        let startCoords: number[] | null = null;
        let endCoords: number[] | null = null;

        this.map.events.add('mousemove', (e: any) => {
            if (!this.selectionMode || !this.selectionStartPoint) {return;}
            const currentCoords = e.get('coords');
            if (!currentCoords) {return;}

            startCoords = [this.selectionStartPoint[0], this.selectionStartPoint[1]];
            endCoords = [currentCoords[0], currentCoords[1]];

            this.selectionRectangle.geometry.setCoordinates([startCoords, endCoords]);
        });

        this.map.events.add('mouseup', (e: any) => {
            if (!this.selectionMode || e.get('button') !== 2) {return;}
            this.selectionMode = false;
            if (this.selectionStartPoint) {
                this.processSelection(startCoords, endCoords); // передаем координаты начала и окончания выделения
            }
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.cancelSelection(); // по нажатию на ESC отменяем выделение
            }
        });

        this.map.events.add('contextmenu', (e: any) => {
            e.preventDefault();
        });
    }

    private processSelection(startCoords: number[] | null, endCoords: number[] | null): void {
        if (!startCoords || !endCoords) {return;}

        const normalizedBounds = [ // нормализуем координаты для определения области в любом порядке
            [Math.min(startCoords[0], endCoords[0]), Math.min(startCoords[1], endCoords[1])],
            [Math.max(startCoords[0], endCoords[0]), Math.max(startCoords[1], endCoords[1])]
        ];

        this.clearHighlights(); // убираем подсветку точек на карте
        this.cancelSelection(); // убираем выделение на шкале
        this.selectedPoints = [];

        this.map.geoObjects.each((geoObject: any) => { // добавляем в массив заявки, попавшие в выделенную область
            const orderId = geoObject.properties.get('orderId');
            if (orderId && geoObject.geometry) {
                const coords = geoObject.geometry.getCoordinates();
                if (this.isPointInBounds(coords, normalizedBounds)) {
                    this.selectedPoints.push(orderId);
                    geoObject.options.set('preset',
                        geoObject.properties.get('iconContent')
                            ? 'islands#greenIcon'
                            : 'islands#greenCircleDotIcon');
                }
            }
        });

        this.selectionChanged.emit({
            orders: this.selectedPoints,
            cancel: false
        });

        this.map.geoObjects.remove(this.selectionRectangle);
        this.selectionRectangle = null;
        this.selectionStartPoint = null;
    }

    private isPointInBounds(point: number[], bounds: number[][]): boolean {
        // Определяем реальные границы прямоугольника (может быть выделение в любом направлении)
        const minLat = Math.min(bounds[0][0], bounds[1][0]);
        const maxLat = Math.max(bounds[0][0], bounds[1][0]);
        const minLon = Math.min(bounds[0][1], bounds[1][1]);
        const maxLon = Math.max(bounds[0][1], bounds[1][1]);

        return point[0] >= minLat &&
            point[0] <= maxLat &&
            point[1] >= minLon &&
            point[1] <= maxLon;
    }

    private clearHighlights(): void { // изменяем иконку на карте
        this.map.geoObjects.each((geoObject: any) => {
            const orderId = geoObject.properties.get('orderId');
            if (orderId) {
                if (geoObject.properties.get('iconContent')) {
                    geoObject.options.set('preset', 'islands#blueIcon');
                } else {
                    geoObject.options.set('preset', 'islands#blackCircleDotIcon');
                }
            }
        });
    }

    private cancelSelection(): void { // очищаем предыдущие координаты. отменяем выделение заявок на шкале
        this.clearHighlights();
        this.selectionChanged.emit({
            orders: this.selectedPoints,
            cancel: true
        });

        if (this.selectionRectangle) {
            this.map.geoObjects.remove(this.selectionRectangle);
            this.selectionRectangle = null;
        }

        this.selectionMode = false;
        this.selectionStartPoint = null;
        this.selectedPoints = [];
    }

    drawMap(points: IMapPoint[]): void {
        // Очистка карты от существующих меток
        this.map?.geoObjects.removeAll();
        // Добавление меток на карту
        if (!points.length) {return;}

        const offset = 0.00007; // Величина смещения в градусах
        const usedCoordinates: number[][] = []; // Массив для хранения уже использованных координат

        points.forEach((place) => {
            let [lat, lon] = place.coordinates;

            while (usedCoordinates.some(coord => coord[0] === lat && coord[1] === lon)) {
                lat += offset; // Смещаем широту
                lon += offset; // Смещаем долготу
            }

            // Добавляем новые координаты в массив использованных
            usedCoordinates.push([lat, lon]);

            const placemark = new ymaps.Placemark([lat, lon], {
                orderId: place.orderId,
                balloonContent: place.label,
                iconContent: place.queueNumber?.toString() || '',
            });

            // Добавляем обработчик события mouseenter для каждой метки
            placemark.events.add('mouseenter', () => {
                this.pointTouched.emit(place.orderId);
            });

            placemark.events.add('mouseleave', () => {
                this.pointTouched.emit(null);
            });

            this.map?.geoObjects.add(placemark);
        });

        const bounds = this.map?.geoObjects.getBounds();
        if (bounds && this.updateMap) {
            this.map.setBounds(bounds, {
                checkZoomRange: true,
                duration: 500
            });
            this.updateMap = false;
        }
    }

    addActiveOrders(points: IMapPoint[]): void {
        // Добавление меток на карту
        if (!points.length) {
            this.handleSelectedOrdersChange();
            return;
        }

        const offset = 0.00009; // Величина смещения в градусах
        const usedCoordinates: number[][] = []; // Массив для хранения уже использованных координат

        points.forEach((place) => {
            let [lat, lon] = place.coordinates;
            while (usedCoordinates.some(coord => coord[0] === lat && coord[1] === lon)) {
                lat += offset; // Смещаем широту
                lon += offset; // Смещаем долготу
            }

            // Добавляем новые координаты в массив использованных
            usedCoordinates.push([lat, lon]);

            const placemark = new ymaps.Placemark([lat, lon], {
                orderId: place.orderId,
                balloonContent: place.label,
            },{preset: 'islands#blackCircleDotIcon'});

            // Добавляем обработчик события mouseenter для каждой метки
            placemark.events.add('mouseenter', () => {
                this.pointTouched.emit(place.orderId);
            });

            placemark.events.add('mouseleave', () => {
                this.pointTouched.emit(null);
            });

            this.map?.geoObjects.add(placemark);
        });
        this.handleSelectedOrdersChange();
    }

    addExecutorsLocation(workersPoints: IExecutorPoint[]): void {
        // Добавление меток на карту
        if (!workersPoints.length) {return;}

        const workers = workersPoints.filter((point) => point.workerId);

        workers.forEach((worker) => {
            const [lat, lon] = worker.coordinates;

            const placemark = new ymaps.Placemark([lat, lon], {
                workerId: worker.workerId,
                balloonContent: worker.workerName,
            },{
                // Настройки кастомной иконки
                iconLayout: 'default#image', // Используем свое изображение
                iconImageHref: '/executor-car.png', // Путь к иконке
                iconImageSize: [32, 32], // Размер (ширина, высота)
                iconImageOffset: [-16, -16], // Смещение (чтобы центр иконки совпадал с координатой)
            });

            // Добавляем обработчик события mouseenter для каждого работника
            placemark.events.add('mouseenter', () => {
                this.workerEntered.emit(worker.workerId);
            });

            placemark.events.add('mouseleave', () => {
                this.workerEntered.emit(null);
            });

            this.map?.geoObjects.add(placemark);
        });

    }

    // Метод для обновления размеров карты
    public updateMapSize(): void {
        if (this.map) {
            this.map.container.fitToViewport();
        }
    }

    highlightMapPoint(orderId: number | null): void {
        this.map?.geoObjects.each((placemark: any) => {
            const currentOrderId = placemark.properties.get('orderId');
            if (!currentOrderId) {return;}

            if (currentOrderId === orderId) {
                placemark.options.set({
                    preset: placemark.properties.get('iconContent')
                        ? 'islands#greenIcon'
                        : 'islands#greenCircleDotIcon',
                });
                const coords = placemark.geometry.getCoordinates();
                this.map.setCenter(coords, undefined, { duration: 500 });
            }
            else if (this.selectedPoints.includes(currentOrderId)) {
                placemark.options.set({
                    preset: placemark.properties.get('iconContent')
                        ? 'islands#greenIcon'
                        : 'islands#greenCircleDotIcon',
                });
            }
            else {
                placemark.options.set({
                    preset: placemark.properties.get('iconContent')
                        ? 'islands#blueIcon'
                        : 'islands#blackCircleDotIcon',
                });
            }
        });
    }

    public calculateNewHeight(startY: number, startHeight: number, clientY: number): number {
        const dy = startY - clientY;
        const newHeight = startHeight + dy;
        // Устанавливаем минимальную и максимальную высоту
        return Math.max(100, Math.min(window.innerHeight - 200, newHeight));
    }
}
